import type { YaasPositionCardData } from "@solomon-apps/shared/src/types/yaas";
import { lpProtocolProvider } from "@solomon-apps/shared/src/yaas/lpProtocols";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import type { RaydiumLpHolding } from "@/hooks/yaas/lpTypes";

import { balancePositions } from "./balancePositions";
import { breakdownTitleFor } from "./breakdownDisplay";

/** A backend position that keeps its own row, and its routing controls. */
export interface PositionBalanceRow {
    kind: "position";
    position: YaasPositionCardData;
    amount: string | null;
}

/** Every LP position in one pool, summed into a single row. */
export interface PoolBalanceRow {
    kind: "pool";
    key: string;
    title: string;
    /** Picks the protocol logo (see breakdownIconFor). */
    iconPool: { pair: string; protocol: string; type: string };
    poolAddress: string;
    /** Summed USDv across the pool's positions, or null when any of them
     *  is unresolved. A partial sum would present a figure as the pool's
     *  whole balance when it is not. */
    amount: string | null;
}

export type BalanceListRow = PositionBalanceRow | PoolBalanceRow;

interface PoolGroup extends Omit<PoolBalanceRow, "kind" | "amount"> {
    positions: YaasPositionCardData[];
    amounts: Array<string | null>;
}

function positionAmount(position: YaasPositionCardData): string | null {
    const { usdvAmountExact, USDvValue } = position.position;
    return usdvAmountExact === undefined ? String(USDvValue) : usdvAmountExact;
}

/**
 * One Balances row per pool, not per position.
 *
 * A concentrated-liquidity pool gives every price range its own
 * position, so one wallet can hold several in the same pool, enrolled
 * (yaas-api Program Positions) or only discovered (holdings). Discovery,
 * backend matching and the total stay per position, because that is what
 * is owned and what yaas-api tracks; only the list collapses them, since
 * three rows reading "GOLD / USDv LP" say nothing a user can tell apart.
 *
 * A pool holding a single enrolled position keeps that position's own
 * row, routing controls included. The wallet row, non-LP positions and
 * LPs without an identity stay one row each. Pools keep the order they
 * are first seen in, after the position rows.
 */
export function balanceListRows(positions: YaasPositionCardData[], holdings: RaydiumLpHolding[]): BalanceListRow[] {
    const rows: PositionBalanceRow[] = [];
    const pools = new Map<string, PoolGroup>();
    for (const position of balancePositions(positions)) {
        const identity = position.position.lpIdentity;
        if (!identity) {
            rows.push({ kind: "position", position, amount: positionAmount(position) });
            continue;
        }
        const key = `${identity.protocol}:${identity.poolAddress}:${identity.observedAssetMint}`;
        const group = pools.get(key);
        if (group) {
            group.positions.push(position);
            group.amounts.push(positionAmount(position));
        } else {
            pools.set(key, {
                key,
                title: breakdownTitleFor(position.pool, identity.protocol),
                iconPool: position.pool,
                poolAddress: identity.poolAddress,
                positions: [position],
                amounts: [positionAmount(position)]
            });
        }
    }
    for (const holding of holdings) {
        const { protocol, poolAddress, observedAssetMint, displayName } = holding.pool;
        const key = `${protocol}:${poolAddress}:${observedAssetMint}`;
        const group = pools.get(key);
        if (group) group.amounts.push(holding.amount);
        else
            pools.set(key, {
                key,
                title: displayName,
                iconPool: { pair: displayName, protocol: lpProtocolProvider(protocol) ?? "", type: "DEX LP" },
                poolAddress,
                positions: [],
                amounts: [holding.amount]
            });
    }
    const poolRows = [...pools.values()].map(({ positions: enrolled, amounts, ...pool }): BalanceListRow => {
        if (enrolled.length === 1 && amounts.length === 1) {
            return { kind: "position", position: enrolled[0], amount: amounts[0] };
        }
        return {
            kind: "pool",
            ...pool,
            amount: amounts.some((amount) => amount === null)
                ? null
                : amounts
                      .reduce((sum, amount) => sum.add(new BigDecimal(amount as string)), new BigDecimal(0))
                      .toString()
        };
    });
    return [...rows, ...poolRows];
}
